// Mirrors backend/render/equations.py::annotate_latex. Pure function.
// Wraps each symbol's first occurrence in `\htmlId{<symbol_id>}{<surface>}`
// so KaTeX's trust:true mode produces a stable per-symbol DOM id.
//
// PRD 10 Phase D — symbols whose Pass-2 reconciliation produced a
// shared `corpus_symbol_id` get an outer `\htmlData{corpus-symbol-id=…}`
// wrapper. KaTeX renders that as `data-corpus-symbol-id="…"` on the
// span, which the doc-level cross-equation coordinator uses to light
// every co-identified symbol when any is hovered. Keep the htmlId
// inside the htmlData so per-symbol DOM ids stay stable.

import type { EquationSymbol } from "../data/types";

export function annotateLatex(
  latex: string,
  symbols: ReadonlyArray<EquationSymbol>,
): string {
  // Longer surfaces first so `\beta_1` matches before standalone `\beta`.
  const sorted = [...symbols].sort((a, b) => b.latex.length - a.latex.length);
  let out = latex;
  let consumed: Array<[number, number]> = [];
  for (const sym of sorted) {
    const idx = firstMatchOutside(out, sym.latex, consumed);
    if (idx < 0) continue;
    const end = idx + sym.latex.length;
    const htmlId = `\\htmlId{${sym.symbol_id}}{${sym.latex}}`;
    const inner = sym.corpus_symbol_id
      ? `\\htmlData{corpus-symbol-id=${sym.corpus_symbol_id}}{${htmlId}}`
      : htmlId;
    // Brace-wrap so subscript/superscript positions (`_\theta`,
    // `^X_1`) accept the replacement as a single grouped argument.
    // KaTeX otherwise rejects `_\htmlData{…}{…}` as "function with no
    // arguments as subscript".
    const replacement = `{${inner}}`;
    out = out.slice(0, idx) + replacement + out.slice(end);
    const shift = replacement.length - (end - idx);
    consumed = shiftSpans(consumed, idx, shift);
    consumed.push([idx, idx + replacement.length]);
  }
  return out;
}

function firstMatchOutside(
  latex: string,
  surface: string,
  consumed: ReadonlyArray<[number, number]>,
): number {
  let start = 0;
  while (true) {
    const idx = latex.indexOf(surface, start);
    if (idx < 0) return -1;
    const end = idx + surface.length;
    if (overlapsAny(idx, end, consumed)) {
      start = end;
      continue;
    }
    if (insideHtmlId(latex, idx)) {
      start = end;
      continue;
    }
    return idx;
  }
}

function overlapsAny(
  start: number,
  end: number,
  spans: ReadonlyArray<[number, number]>,
): boolean {
  return spans.some(([s, e]) => start < e && s < end);
}

const HTMLID_ARG_RE = /\\htmlId\{[^}]*\}\{/g;

function insideHtmlId(latex: string, idx: number): boolean {
  for (const m of latex.matchAll(HTMLID_ARG_RE)) {
    const openerEnd = (m.index ?? 0) + m[0].length;
    if (openerEnd > idx) continue;
    // Find matching closing brace from openerEnd.
    let depth = 1;
    let j = openerEnd;
    while (j < latex.length && depth > 0) {
      const ch = latex[j];
      if (ch === "{") depth += 1;
      else if (ch === "}") depth -= 1;
      j += 1;
    }
    const closePos = j - 1;
    if (openerEnd <= idx && idx < closePos) return true;
  }
  return false;
}

function shiftSpans(
  spans: ReadonlyArray<[number, number]>,
  anchor: number,
  shift: number,
): Array<[number, number]> {
  return spans.map(([s, e]) => (s >= anchor ? [s + shift, e + shift] : [s, e]));
}
